import ReactGA from 'react-ga4';

// Initialize with your GA4 Measurement ID
// Replace 'G-XXXXXXXXXX' with your actual GA4 Measurement ID
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

export const initGA = () => {
  ReactGA.initialize(MEASUREMENT_ID, {
    testMode: process.env.NODE_ENV !== 'production',
  });
};

// Page view tracking
export const trackPageView = (path: string, title?: string) => {
  ReactGA.send({
    hitType: 'pageview',
    page: path,
    title: title,
  });
};

// Custom event tracking
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  ReactGA.event({
    category,
    action,
    label,
    value,
  });
};

// Trip Wizard Events
export const trackCountrySelected = (countryId: number, countryName?: string) => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'select_country',
    label: countryName || String(countryId),
  });
};

export const trackCitiesSelected = (cityCount: number, cityNames?: string[]) => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'select_cities',
    label: cityNames?.join(', '),
    value: cityCount,
  });
};

export const trackSelectAllCities = (cityCount: number) => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'select_all_cities',
    value: cityCount,
  });
};

export const trackDaysSelected = (days: number) => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'select_days',
    value: days,
  });
};

export const trackPreferencesSet = (preferences: {
  budget?: number;
  travelers?: number;
  travelMode?: string;
  cuisineTypes?: string[];
  activityTypes?: string[];
}) => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'set_preferences',
    label: JSON.stringify({
      hasBudget: !!preferences.budget,
      travelers: preferences.travelers,
      travelMode: preferences.travelMode,
      cuisineCount: preferences.cuisineTypes?.length || 0,
      activityCount: preferences.activityTypes?.length || 0,
    }),
  });
};

// Trip Build Events
export const trackBuildTripStarted = (params: {
  countryId: number;
  cityCount: number;
  days: number;
}) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_started',
    label: `${params.cityCount} cities, ${params.days} days`,
    value: params.days,
  });
};

export const trackBuildTripCompleted = (tripId: number, duration: number) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_completed',
    label: String(tripId),
    value: Math.round(duration / 1000), // duration in seconds
  });
};

export const trackBuildTripFailed = (error: string) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_failed',
    label: error,
  });
};

// Text-based Trip Build Events
export const trackBuildTripFromTextStarted = (textLength: number) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_from_text_started',
    value: textLength,
  });
};

export const trackBuildTripFromTextCompleted = (tripId: number, duration: number) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_from_text_completed',
    label: String(tripId),
    value: Math.round(duration / 1000),
  });
};

export const trackBuildTripFromTextFailed = (error: string) => {
  ReactGA.event({
    category: 'Trip Build',
    action: 'build_trip_from_text_failed',
    label: error,
  });
};

export const trackTripModeSelected = (mode: 'wizard' | 'text') => {
  ReactGA.event({
    category: 'Trip Wizard',
    action: 'mode_selected',
    label: mode,
  });
};

// Trip Management Events
export const trackTripSaved = (tripId: number) => {
  ReactGA.event({
    category: 'Trip Management',
    action: 'trip_saved',
    label: String(tripId),
  });
};

export const trackTripRebuilt = (tripId: number) => {
  ReactGA.event({
    category: 'Trip Management',
    action: 'trip_rebuilt',
    label: String(tripId),
  });
};

export const trackTripDeleted = (tripId: number) => {
  ReactGA.event({
    category: 'Trip Management',
    action: 'trip_deleted',
    label: String(tripId),
  });
};

export const trackTripViewed = (tripId: number, tripName?: string) => {
  ReactGA.event({
    category: 'Trip Management',
    action: 'trip_viewed',
    label: tripName || String(tripId),
  });
};

// User Events
export const trackLogin = () => {
  ReactGA.event({
    category: 'User',
    action: 'login',
  });
};

export const trackLogout = () => {
  ReactGA.event({
    category: 'User',
    action: 'logout',
  });
};

// Navigation Events
export const trackNavigationClick = (destination: string) => {
  ReactGA.event({
    category: 'Navigation',
    action: 'click',
    label: destination,
  });
};

// POI Events
export const trackPOIAdded = (poiId: number, poiName?: string) => {
  ReactGA.event({
    category: 'POI',
    action: 'poi_added_to_trip',
    label: poiName || String(poiId),
  });
};

export const trackPOIRemoved = (poiId: number) => {
  ReactGA.event({
    category: 'POI',
    action: 'poi_removed_from_trip',
    label: String(poiId),
  });
};

export default ReactGA;
